import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import ImageGallery from "./tire-info/ImageGallery";
import { useTextColorClass } from "@/components/ui/text-coloring";
import { useIsMobile } from "@/hooks/use-mobile";
import { VehicleTestImages } from "@/types/vehicleTest";
import { useLanguage } from "@/context/LanguageContext";
import { carDetailsLabels } from "@/i18n";
import { GuestLockedSection } from "@/components/auth/GuestLockedSection";

interface TiresSectionProps {
  images: VehicleTestImages["tires"];
  guestLocked?: boolean;
  onGuestLoginRequired?: () => void;
  /** When false, render content expanded (no accordion), e.g. for document/PDF view. */
  collapsible?: boolean;
  /** When true, show primary and secondary tire sets in one column (no tabs). Used in DocumentView. */
  singleColumnLayout?: boolean;
  /** Optional: open clicked image in parent lightbox (DocumentView) */
  onOpenImage?: (args: { url: string; alt: string; index: number }) => void;
  testData?: {
    tires?: {
      primary?: {
        frontDepth?: string;
        rearDepth?: string;
      };
      secondary?: {
        frontDepth?: string;
        rearDepth?: string;
      };
    };
    brakes?: {
      frontEffectiveness?: string;
      rearEffectiveness?: string;
    };
    vehicleDetails?: {
      currentTireType?: string;
      currentRimType?: string;
      extraTireSet?: boolean;
      extraTireType?: string;
      extraRimType?: string;
    };
  };
}

const TiresSection = ({
  images,
  testData,
  collapsible = true,
  singleColumnLayout = false,
  onOpenImage,
  guestLocked = false,
  onGuestLoginRequired,
}: TiresSectionProps) => {
  const { baseText, subtleText } = useTextColorClass();
  const isMobile = useIsMobile();
  const { language } = useLanguage();
  const w = carDetailsLabels(language).wheels;

  // Extract data from the correct structure
  const brakes = testData?.brakes;
  const tires = testData?.tires;
  const vehicleDetails = testData?.vehicleDetails;

  // Convert tire images to string arrays
  const getTireImageUrls = (
    images: VehicleTestImages["tires"] | undefined,
    isPrimary: boolean
  ): string[] => {
    if (!images) return [];

    const tireSet = isPrimary ? images.primary : images.secondary;
    if (!tireSet) return [];

    // Handle new array-based structure
    if (Array.isArray(tireSet)) {
      return tireSet.map((tire) => tire.url).filter(Boolean);
    }

    return [];
  };

  const getTireFullscreenUrls = (
    images: VehicleTestImages["tires"] | undefined,
    isPrimary: boolean,
  ): string[] => {
    if (!images) return [];
    const tireSet = isPrimary ? images.primary : images.secondary;
    if (!tireSet || !Array.isArray(tireSet)) return [];
    return tireSet
      .map((tire) => (tire as any).fullSizeUrl || tire.url)
      .filter(Boolean);
  };

  // Prepare image URLs for ImageGallery
  const primaryImageUrls = getTireImageUrls(images, true);
  const secondaryImageUrls = getTireImageUrls(images, false);
  const primaryFullscreenUrls = getTireFullscreenUrls(images, true);
  const secondaryFullscreenUrls = getTireFullscreenUrls(images, false);

  // Check if we have any tire images
  const hasPrimaryTires = primaryImageUrls.length > 0;
  const hasSecondaryTires = secondaryImageUrls.length > 0;
  
  // Check if we have secondary tire info: extraTireSet from API, secondary images, or fallback from extra types / testData.tires.secondary
  const hasSecondaryFromApi = vehicleDetails?.extraTireSet === true;
  const hasSecondaryImages = secondaryImageUrls.length > 0;
  const hasSecondaryFromTypes =
    !!(vehicleDetails?.extraTireType?.trim?.() || vehicleDetails?.extraRimType?.trim?.());
  const hasSecondaryFromTestData =
    !!(tires?.secondary?.frontDepth?.trim?.() || tires?.secondary?.rearDepth?.trim?.());
  const hasSecondaryTireInfo =
    hasSecondaryFromApi || hasSecondaryImages || hasSecondaryFromTypes || hasSecondaryFromTestData;

  const hasAnyTires = hasPrimaryTires || hasSecondaryTireInfo;

  const getTranslatedTireType = (raw?: string | null) => {
    if (!raw?.trim()) return w.notSpecified;
    const types = w.tireTypes as Record<string, string>;
    return types[raw] ?? raw;
  };

  const getTranslatedRimType = (raw?: string | null) => {
    if (!raw?.trim()) return w.notSpecified;
    const types = w.rimTypes as Record<string, string>;
    return types[raw] ?? raw;
  };

  const primaryBlock = (
    <div className="space-y-4">
      {hasPrimaryTires ? (
        <ImageGallery
          images={primaryImageUrls}
          fullscreenImages={primaryFullscreenUrls}
          title={isMobile ? w.primarySetShort : w.primarySet}
          onOpenImage={onOpenImage}
        />
      ) : (
        <p className={`${subtleText} text-sm md:text-sm`}>
          {w.noImagesPrimary}
        </p>
      )}
      <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-0 md:divide-x md:divide-gray-200 md:dark:divide-gray-700">
          <div className="space-y-2 md:pr-4">
            <h4 className="text-sm font-medium">{w.wheelInfo}</h4>
            <div className="space-y-2">
              <div className="flex justify-between gap-2">
                <span className="text-sm text-primary/60">{w.type}:</span>
                <span className="text-sm font-medium text-right">
                  {getTranslatedTireType(vehicleDetails?.currentTireType)}
                </span>
              </div>
              <div className="flex justify-between gap-2">
                <span className="text-sm text-primary/60">{w.rim}:</span>
                <span className="text-sm font-medium text-right">
                  {getTranslatedRimType(vehicleDetails?.currentRimType)}
                </span>
              </div>
            </div>
          </div>
          <div className="space-y-2 md:px-4 pt-3 border-t border-gray-200 dark:border-gray-700 md:border-t-0 md:pt-0">
            <h4 className="text-sm font-medium">{w.brakeCondition}</h4>
            <div className="space-y-2">
              <div className="flex justify-between gap-2">
                <span className="text-sm text-primary/60">{w.front}:</span>
                <span className="text-sm font-medium text-right">
                  {brakes?.frontEffectiveness || w.notSpecified}
                </span>
              </div>
              <div className="flex justify-between gap-2">
                <span className="text-sm text-primary/60">{w.rear}:</span>
                <span className="text-sm font-medium text-right">
                  {brakes?.rearEffectiveness || w.notSpecified}
                </span>
              </div>
            </div>
          </div>
          <div className="space-y-2 md:pl-4 pt-3 border-t border-gray-200 dark:border-gray-700 md:border-t-0 md:pt-0">
            <h4 className="text-sm font-medium">{w.treadDepth}</h4>
            <div className="space-y-2">
              <div className="flex justify-between gap-2">
                <span className="text-sm text-primary/60">{w.front}:</span>
                <span className="text-sm font-medium text-right">
                  {tires?.primary?.frontDepth || w.notSpecified}
                </span>
              </div>
              <div className="flex justify-between gap-2">
                <span className="text-sm text-primary/60">{w.rear}:</span>
                <span className="text-sm font-medium text-right">
                  {tires?.primary?.rearDepth || w.notSpecified}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const secondaryBlock = (
    <div className="space-y-4">
      {hasSecondaryTires ? (
        <ImageGallery
          images={secondaryImageUrls}
          fullscreenImages={secondaryFullscreenUrls}
          title={isMobile ? w.secondarySetShort : w.secondarySet}
          onOpenImage={onOpenImage}
        />
      ) : (
        <p className={`${subtleText} text-sm md:text-sm`}>
          {w.noImagesSecondary}
        </p>
      )}
      <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-0 md:divide-x md:divide-gray-200 md:dark:divide-gray-700">
          <div className="space-y-2 md:pr-4">
            <h4 className="text-sm font-medium">{w.wheelInfo}</h4>
            <div className="space-y-2">
              <div className="flex justify-between gap-2">
                <span className="text-sm text-primary/60">{w.type}:</span>
                <span className="text-sm font-medium text-right">
                  {getTranslatedTireType(vehicleDetails?.extraTireType)}
                </span>
              </div>
              <div className="flex justify-between gap-2">
                <span className="text-sm text-primary/60">{w.rim}:</span>
                <span className="text-sm font-medium text-right">
                  {getTranslatedRimType(vehicleDetails?.extraRimType)}
                </span>
              </div>
            </div>
          </div>
          <div className="space-y-2 md:px-4 pt-3 border-t border-gray-200 dark:border-gray-700 md:border-t-0 md:pt-0">
            <h4 className="text-sm font-medium">{w.brakeCondition}</h4>
            <div className="space-y-2">
              <div className="flex justify-between gap-2">
                <span className="text-sm text-primary/60">{w.front}:</span>
                <span className="text-sm font-medium text-right">
                  {brakes?.frontEffectiveness || w.notSpecified}
                </span>
              </div>
              <div className="flex justify-between gap-2">
                <span className="text-sm text-primary/60">{w.rear}:</span>
                <span className="text-sm font-medium text-right">
                  {brakes?.rearEffectiveness || w.notSpecified}
                </span>
              </div>
            </div>
          </div>
          <div className="space-y-2 md:pl-4 pt-3 border-t border-gray-200 dark:border-gray-700 md:border-t-0 md:pt-0">
            <h4 className="text-sm font-medium">{w.treadDepth}</h4>
            <div className="space-y-2">
              <div className="flex justify-between gap-2">
                <span className="text-sm text-primary/60">{w.front}:</span>
                <span className="text-sm font-medium text-right">
                  {tires?.secondary?.frontDepth &&
                  tires.secondary.frontDepth !== "null" &&
                  tires.secondary.frontDepth.trim() !== ""
                    ? tires.secondary.frontDepth
                    : w.notSpecified}
                </span>
              </div>
              <div className="flex justify-between gap-2">
                <span className="text-sm text-primary/60">{w.rear}:</span>
                <span className="text-sm font-medium text-right">
                  {tires?.secondary?.rearDepth &&
                  tires.secondary.rearDepth !== "null" &&
                  tires.secondary.rearDepth.trim() !== ""
                    ? tires.secondary.rearDepth
                    : w.notSpecified}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const tireContent = singleColumnLayout ? (
    <div className="space-y-6">
      <div>
        <h3 className="text-base font-medium mb-3">
          {isMobile ? w.primarySetShort : w.primarySet}
        </h3>
        {primaryBlock}
      </div>
      {hasSecondaryTireInfo && (
        <div>
          <h3 className="text-base font-medium mb-3">
            {isMobile ? w.secondarySetShort : w.secondarySet}
          </h3>
          {secondaryBlock}
        </div>
      )}
    </div>
  ) : (
    <div className="space-y-4">
      <Tabs defaultValue="primary" className="w-full">
        <TabsList className={`grid w-full ${hasSecondaryTireInfo ? "grid-cols-2" : "grid-cols-1"}`}>
          <TabsTrigger value="primary">
            {isMobile ? w.primarySetShort : w.primarySet}
          </TabsTrigger>
          {hasSecondaryTireInfo && (
            <TabsTrigger value="secondary">
              {isMobile ? w.secondarySetShort : w.secondarySet}
            </TabsTrigger>
          )}
        </TabsList>
        <TabsContent value="primary" className="space-y-4 mt-4">
          {primaryBlock}
        </TabsContent>
        <TabsContent value="secondary" className="space-y-4 mt-4">
          {secondaryBlock}
        </TabsContent>
      </Tabs>
    </div>
  );

  const displayContent = (
    <GuestLockedSection
      locked={guestLocked}
      onLoginRequired={onGuestLoginRequired}
    >
      {guestLocked ? <div className="min-h-[8rem]" aria-hidden /> : tireContent}
    </GuestLockedSection>
  );

  if (!collapsible) {
    return (
      <div className={`rounded-lg backdrop-blur border bg-card text-card-foreground shadow-sm animate-fade-in`}>
        <div className="p-4 md:p-6">
          <h2 className={`font-semibold text-sm md:text-base mb-4 ${baseText}`}>{w.title}</h2>
          {displayContent}
        </div>
      </div>
    );
  }

  return (
    <div className={`rounded-lg backdrop-blur border bg-card text-card-foreground shadow-sm animate-fade-in`}>
      <Accordion type="single" collapsible className="w-full">
        <AccordionItem value="tire-images">
          <AccordionTrigger className={`px-4 py-4 md:py-3 hover:bg-accent/20 flex justify-between`}>
            <span className={`font-semibold text-base md:text-base ${baseText}`}>{w.title}</span>
          </AccordionTrigger>
          <AccordionContent className={`p-4`}>
            {displayContent}
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
};

export default TiresSection;
